import { ActionCreatorWithPayload } from '@reduxjs/toolkit';
import {
    findHub,
    hubNotFound,
    ISignalRHub,
    mergeMapHubToAction,
    signalrHubUnstarted,
    startSignalRHub
} from '@tpn/redux-observable-signalr-core';
import { AnyAction } from 'redux';
import { StateObservable } from 'redux-observable';
import { merge, Observable, of } from 'rxjs';
import { filter, ignoreElements, map, mergeMap, withLatestFrom } from 'rxjs/operators';
import { isActionOf } from 'typesafe-actions';
import { RootEpic, RootState } from '../../store';
import { hasPermission } from '../../utils';
import { authProvider } from '../auth/authProvider';
import { ResourceType } from '../PricePrgClient';
import { SignalrHub } from './signalrTypes';

export const hubListener =
    (
        hub: SignalrHub,
        registerListeners: (hub: ISignalRHub, state$: StateObservable<RootState>) => Observable<AnyAction>[]
    ): RootEpic =>
    (action$, state$) =>
        action$.pipe(
            filter(isActionOf(signalrHubUnstarted)),
            filter(h => h.hubName === hub.hubName),
            mergeMapHubToAction(({ hub: h }) => {
                const actions$ = registerListeners(h, state$);
                return merge(...actions$, of(startSignalRHub(hub)));
            })
        );

export function hubNotifier<TPayload>(
    hub: SignalrHub,
    actionCreator: ActionCreatorWithPayload<TPayload, string>,
    sendAction: (hub: ISignalRHub, payload: TPayload) => Observable<unknown>
): RootEpic {
    return action$ =>
        action$.pipe(
            filter(actionCreator.match),
            mergeMap(action => {
                const h = findHub(hub);

                if (!h) {
                    return of(hubNotFound(hub));
                }

                return sendAction(h, action.payload);
            }),
            // Dont forward the observable from hub.send since its not an action
            ignoreElements()
        );
}

export function guardedAction<TPayload>(
    resourceType: ResourceType,
    hub: ISignalRHub,
    state$: StateObservable<RootState>,
    action: string,
    ac: ActionCreatorWithPayload<TPayload>
) {
    return hub.on<TPayload>(action).pipe(
        withLatestFrom(state$.pipe(map(state => state.user.currentUserPermissions))),
        filter(([, cup]) => hasPermission(cup, resourceType)),
        map(([payload]) => ac(payload))
    );
}

export const signalROptions = {
    accessTokenFactory: async () => {
        const accounts = authProvider.getAllAccounts();
        const accessToken = await authProvider?.acquireTokenSilent({
            account: accounts[0],
            scopes: ['User.Read', `api://${process.env.REACT_APP_API}/PricePrg.User`]
        });
        return accessToken.idToken;
    }
};
